import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  User,
  Role,
  CreateUserData,
  UpdateUserData,
} from '../types/user';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getRoles = async (): Promise<Role[]> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLES}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch roles');
  }

  const result = await response.json();
  return result.data ?? [];
};

export const getAllUsers = async (): Promise<User[]> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS.GET_ALL}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch users');
  }

  const result = await response.json();
  return result.data ?? [];
};

export const getUserById = async (id: number): Promise<User> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS.GET_BY_ID}/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch user');
  }

  const result = await response.json();
  return result.data;
};

export const createUser = async (userData: CreateUserData): Promise<User> => {
  const payload: CreateUserData = {
    name: userData.name,
    email: userData.email,
    password: userData.password,
    role: userData.role,
    ...(userData.active !== undefined && { active: userData.active }),
  };

  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create user');
  }

  const result = await response.json();
  return result.data;
};

export const updateUser = async (userId: number, userData: UpdateUserData): Promise<User> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS.UPDATE}/${userId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update user');
  }

  const result = await response.json();
  return result.data;
};

export const deleteUser = async (userId: number): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS.DELETE}/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete user');
  }
};
